import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import React, { useCallback, useEffect, useMemo } from "react";
import { TableHeaderColumn } from "react-bootstrap-table";
import { useDataHook } from "../../../hooks/useDataHook";
import { dateTimeReportFormatter } from "../../fomatters/DateFormatter";
import { textFormatter } from "../../fomatters/TextFormatter";
import DataLabel from "../DataLabel";
import List from "../List";

const resultMap = {
  0: "LOSE",
  1: "WIN",
  2: "PUSH",
  255: "PENDING",
};

const lineStyleMap = {
  E: "Eastern",
};

function WagerDetails({ WagerId, Graded, Open, onClose }) {
  //#region OPEN /CLOSE
  const [open, setOpen] = React.useState(Open);
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    setOpen(false);
  }, [onClose]);
  //#endregion

  useEffect(() => {
    setOpen(Open);
  }, [Open]);

  //#region EVENT INFO
  const wagerHeaderPath = useMemo(
    () =>
      Graded
        ? `/Wager/WagerDetail_GetGradedWagerHeader?idWager=${WagerId}`
        : `/Wager/WagerDetail_GetWagerHeader?idWager=${WagerId}`,
    [WagerId, Graded]
  );
  const wagerDetailsPath = useMemo(
    () =>
      Graded
        ? `/Wager/WagerDetail_GetGradedWagerDetail?idWager=${WagerId}`
        : `/Wager/WagerDetail_GetOpenWagerDetail?idWager=${WagerId}`,
    [WagerId, Graded]
  );

  const [selectedDetail, setSelectedDetail] = React.useState({});

  /**
   * @type {{value: {
   * LineType: string;
   * ProfileName: string;
   * WagerTypeDescription: string;
   * IdPlayer: number;
   * TicketNumber: number;
   * PlacedDate: string; // or Date if you plan to parse it
   * PhoneLine: number;
   * RiskAmount: number;
   * WinAmount: number;
   * IfBetWagerType: number;
   * SettledDate: string; // or Date if you plan to parse it
   * CompleteDescription: string;
   * OriginalWagerAmount: number;
   * OriginalRiskAmount: number;
   * OriginalWinAmount: number;
   * ChartPct: number;
   * PointsPurchased: number;
   * TaxAmount: number;
   * WagerType: string;
   * Result: string;
   * RiskWin: number;
   * LastModification: string; // or Date if you plan to parse it
   * IP: string;
   * LineStyle: string;
   * LoginName: string;
   * Player: string;
   * Symbol: string;
   * DeleteReason: string;
   * FreePlay: number;
   * }, loading: boolean}}
   */
  const { value: wagerHeader, loading: loadingWagerHeader } = useDataHook({
    path: wagerHeaderPath,
  });

  //#endregion

  const getProcess = useCallback(
    (data) =>
      data.map((item) => ({
        ...item,
        Detail: [
          item.Description,
          item.Description !== item.CompleteDescription
            ? item.CompleteDescription
            : undefined,
        ].filter(Boolean),
        Teams: item.VisitorNumber
          ? [
              item.GameDescription,
              `[${item.VisitorNumber}] ${item.VisitorTeam}`,
              item.VisitorTeam !== item.HomeTeam
                ? `[${item.HomeNumber}] ${item.HomeTeam}`
                : undefined,
            ].filter(Boolean)
          : [],
        Score: item.VisitorNumber ? [item.VisitorScore, item.HomeScore] : [],
      })),
    []
  );

  if (!wagerHeader || loadingWagerHeader) return <></>;
  return (
    <Dialog open={open} onClose={handleClose} disableEnforceFocus>
      <DialogTitle>Wager Details for Ticket #{WagerId}</DialogTitle>

      <DialogContent>
        <div>
          <div className="grid grid-cols-3 gap-2">
            <DataLabel
              title="Wager Description"
              value={wagerHeader.CompleteDescription}
              className="col-span-3"
            />
            <DataLabel title="Line Type" value={wagerHeader.LineType} />
            <DataLabel title="Risk Amount" value={wagerHeader.RiskAmount} />
            <DataLabel
              title="Original Wager Amount"
              value={wagerHeader.OriginalWagerAmount}
            />

            <DataLabel title="Wager Type" value={wagerHeader.WagerType} />
            <DataLabel title="Win Amount" value={wagerHeader.WinAmount} />
            <DataLabel
              title="Original Risk Amount"
              value={wagerHeader.OriginalRiskAmount}
            />

            <DataLabel title="Player Account" value={wagerHeader.Player} />
            <DataLabel title="Tax Amount" value={wagerHeader.TaxAmount} />
            <DataLabel
              title="Original Win Amount"
              value={wagerHeader.OriginalWinAmount}
            />

            <DataLabel title="Player Profile" value={wagerHeader.ProfileName} />
            <DataLabel
              title="Points Purchased"
              value={wagerHeader.PointsPurchased}
            />
            <DataLabel
              title="Line Style"
              value={
                lineStyleMap[wagerHeader.LineStyle] ?? wagerHeader.LineStyle
              }
            />

            <DataLabel
              title="Placed Date"
              value={dateTimeReportFormatter(wagerHeader.PlacedDate)}
            />
            <DataLabel
              title="Graded Date"
              value={dateTimeReportFormatter(wagerHeader.LastModification)}
            />
            <DataLabel
              title="Settled Date"
              value={dateTimeReportFormatter(wagerHeader.SettledDate)}
            />

            <DataLabel
              title="Delete Reason"
              value={wagerHeader.DeleteReason}
              className="col-span-3"
            />
          </div>
          <div className="table-responsive table-wrapper-scroll-y my-custom-scrollbar">
            <List
              className="w-full"
              getUrl={wagerDetailsPath}
              itemKey="IdWagerDetail"
              getProcess={getProcess}
              itemSelected={setSelectedDetail}
              pagination={false}
            >
              <TableHeaderColumn
                dataSort
                dataField="IdWagerDetail"
                isKey
                hidden
              >
                Id
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataField="Detail"
                dataFormat={textFormatter}
                width="500"
              >
                Detail
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataField="Result"
                dataFormat={(cell) => resultMap[cell] ?? cell}
              >
                Status
              </TableHeaderColumn>
              <TableHeaderColumn dataSort dataField="LineFactor">
                Line Factor
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataField="GameDateTime"
                dataFormat={dateTimeReportFormatter}
                width="200"
              >
                Game Date
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataField="Teams"
                dataFormat={textFormatter}
                width="500"
              >
                Teams
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataField="Score"
                dataFormat={textFormatter}
              >
                Score
              </TableHeaderColumn>
              <TableHeaderColumn dataSort dataField="DerivativePoints">
                Derivative Points
              </TableHeaderColumn>
            </List>
          </div>
          <DataLabel
            title="Void Reason"
            value={selectedDetail.VoidReason}
            className="col-span-3"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          style={{ height: "36px" }}
          onClick={handleClose}
          color="secondary"
          variant="contained"
          autoFocus
        >
          Cancel
        </Button>
        <Button style={{ height: "36px" }} color="primary" variant="contained">
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WagerDetails;
